import type { Config } from "@/models/AppStateProvider";

export const randomString = (length: number) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

const makeRSVPLink = ({ url, config, myAccount }: { url: string; config: Config; myAccount: string }) => {
  const { pathname } = new URL(url);
  const domain = config.baseDomain.replace("api", "rsvp");
  const inviteUrl = `${domain}${pathname}?referringAccount=${myAccount}`;

  return inviteUrl;
};

export const isValidLink = (url: string) => {
  return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    url,
  );
};

export const pluralValue = (value: number) => (value === 0 || value > 1 ? "s" : "");

export const isValidEncodedBase64 = (src: string) => {
  return /^[0-9a-zA-Z+\/]+[=]{0,3}$/.test(src);
};

export const truncate = (string: string, number?: number, dots?: string) =>
  string && string?.length && string?.length > number ? `${string.substring(0, number)}${dots}` : string;

export const convertCamelCaseToCapitalize = (string: string): string => {
  const result = string?.replace(/([A-Z])/g, " $1");
  return `${result?.charAt(0).toUpperCase()}${result?.slice(1)}`?.trim()?.replace(/ {2}/g, " ");
};

export const convertSnakeCaseToNoCaseCapitalize = (string: string): string =>
  string
    ?.split("_")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    ?.join(" ")
    ?.trim()
    ?.replace(/ {2}/g, " ");

export const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
export const capitalizeFirstLetterOfEachWord = (words: string): string =>
  words.split(" ")?.map(capitalizeFirstLetter).join(" ");

export const isVersionGreater = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  const aParts = a.split(".").map(Number);
  const bParts = b.split(".").map(Number);
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aNum = aParts[i] || 0;
    const bNum = bParts[i] || 0;
    if (aNum > bNum) return true;
    if (aNum < bNum) return false;
  }
  return false;
};

export const lowerCaseToCapCase = (input: string): string => {
  // handle two letter acronyms, like hr -> HR
  if (input?.length === 2) {
    return input.toUpperCase();
  }

  const withSpaces = input
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

  // Capitalize first letter of each word
  return withSpaces
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const normalizeString = (str: string): string =>
  str
    ?.toLowerCase()
    ?.replace(/\s+/g, "")
    ?.replace(/[^a-z0-9]/g, "");

export const matchesFilter = (searchString: string, filter: string): boolean =>
  normalizeString(searchString)?.includes(normalizeString(filter));
